// src/pages/Clients.jsx
// Fully responsive clients page with mobile card view

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  getClients,
  addClient,
  updateClient,
  deleteClient,
  getExportCsvUrl,
  getImportHistory,
  reverseImport,
} from "../api";
import ClientFormFull from "../components/ClientFormFull";

import DuplicateMergeModal from "../components/DuplicateMergeModal";
import {
  History,
  RotateCcw,
  Clock,
  ChevronDown,
  ChevronUp,
  Loader2,
  Search,
  Plus,
  Download,
  Upload,
  Trash2,
  Mail,
  MessageSquare,
  MoreHorizontal,
  Phone,
  User,
  ChevronRight,
  X,
  Filter,
  SlidersHorizontal,
} from "lucide-react";

/* ---------- Modal Component ---------- */
function Modal({ title, children, onClose, width = "max-w-2xl" }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative w-full ${width} bg-white rounded-t-2xl sm:rounded-2xl shadow-xl ring-1 ring-black/5 max-h-[90vh] overflow-hidden flex flex-col`}>
        <div className="h-2 bg-amber-400 rounded-t-2xl sm:rounded-t-2xl flex-shrink-0" />
        <div className="p-4 sm:p-6 overflow-y-auto flex-1">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-800">{title}</h2>
            <button className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-full" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

function Confirm({ text, onConfirm, onCancel, t, loading = false }) {
  return (
    <Modal title={t('pleaseConfirm')} onClose={loading ? undefined : onCancel} width="max-w-md">
      <p className="text-slate-600 mb-4">{text}</p>
      <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
        <button
          className="px-4 py-3 sm:py-2 text-sm rounded-xl border border-gray-300 hover:bg-gray-50 disabled:opacity-50 font-medium"
          onClick={onCancel}
          disabled={loading}
        >
          {t('cancel')}
        </button>
        <button
          className="px-4 py-3 sm:py-2 text-sm rounded-xl bg-red-600 text-white hover:opacity-95 disabled:opacity-50 font-medium"
          onClick={onConfirm}
          disabled={loading}
        >
          {loading ? t('deleting') || 'Deleting...' : t('delete')}
        </button>
      </div>
    </Modal>
  );
}

/* ---------- Mobile Client Card ---------- */
function ClientCard({ client, selected, onSelect, onEdit, onDelete, t }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Checkbox */}
          <input
            type="checkbox"
            checked={selected}
            onChange={(e) => onSelect(e.target.checked)}
            className="mt-1 w-5 h-5 rounded border-slate-300"
          />

          {/* Client Info */}
          <Link to={`/clients/${client.id}`} className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                <User size={16} className="text-slate-500" />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-slate-900 truncate">
                  {client.firstName} {client.lastName}
                </h3>
                {client.carrier && (
                  <p className="text-xs text-slate-500 truncate">{client.carrier}</p>
                )}
              </div>
            </div>

            {/* Contact Info */}
            <div className="mt-2 space-y-1">
              {client.phone && (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Phone size={14} className="text-slate-400 flex-shrink-0" />
                  <span className="truncate">{client.phone}</span>
                </div>
              )}
              {client.email && (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Mail size={14} className="text-slate-400 flex-shrink-0" />
                  <span className="truncate">{client.email}</span>
                </div>
              )}
            </div>
          </Link>

          {/* Arrow */}
          <Link to={`/clients/${client.id}`} className="p-2 text-slate-400">
            <ChevronRight size={20} />
          </Link>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex border-t border-slate-100">
        <button
          onClick={() => onEdit(client)}
          className="flex-1 py-3 text-sm text-blue-600 font-medium hover:bg-blue-50 transition"
        >
          {t('edit')}
        </button>
        <div className="w-px bg-slate-100" />
        <button
          onClick={() => onDelete(client.id)}
          className="flex-1 py-3 text-sm text-red-600 font-medium hover:bg-red-50 transition"
        >
          {t('delete')}
        </button>
      </div>
    </div>
  );
}

/* ---------- Filter Panel Component ---------- */
function FilterPanel({ filters, setFilters, filterOptions, onClear, activeCount }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-white rounded-xl shadow-sm ring-1 ring-black/5 overflow-hidden">
      {/* Filter Toggle Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition"
      >
        <div className="flex items-center gap-2">
          <SlidersHorizontal size={18} className="text-slate-500" />
          <span className="font-medium text-slate-700">Filters</span>
          {activeCount > 0 && (
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
              {activeCount} active
            </span>
          )}
        </div>
        <ChevronDown size={18} className={`text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
      </button>

      {/* Filter Options */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-slate-100">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
            {/* State Filter */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">State</label>
              <select
                value={filters.state || ''}
                onChange={(e) => setFilters(f => ({ ...f, state: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All States</option>
                {filterOptions.states.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            {/* City Filter */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">City</label>
              <select
                value={filters.city || ''}
                onChange={(e) => setFilters(f => ({ ...f, city: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Cities</option>
                {filterOptions.cities.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            {/* Carrier Filter */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Carrier</label>
              <select
                value={filters.carrier || ''}
                onChange={(e) => setFilters(f => ({ ...f, carrier: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Carriers</option>
                {filterOptions.carriers.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            {/* Plan Filter */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Plan</label>
              <select
                value={filters.plan || ''}
                onChange={(e) => setFilters(f => ({ ...f, plan: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Plans</option>
                {filterOptions.plans.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>

            {/* Plan Type Filter */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Plan Type</label>
              <select
                value={filters.planType || ''}
                onChange={(e) => setFilters(f => ({ ...f, planType: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Plan Types</option>
                {filterOptions.planTypes.map(pt => (
                  <option key={pt} value={pt}>{pt}</option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Status</label>
              <select
                value={filters.status || ''}
                onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Statuses</option>
                {filterOptions.statuses.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            {/* Risk Level Filter */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Risk Level</label>
              <select
                value={filters.riskLevel || ''}
                onChange={(e) => setFilters(f => ({ ...f, riskLevel: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Risk Levels</option>
                <option value="stable">Stable (0-34)</option>
                <option value="medium">Medium (35-49)</option>
                <option value="elevated">Elevated (50-64)</option>
                <option value="high">High (65-79)</option>
                <option value="critical">Critical (80-89)</option>
                <option value="severe">Severe (90-100)</option>
              </select>
            </div>

            {/* Effective Date Filter */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Effective Date</label>
              <select
                value={filters.effectiveDate || ''}
                onChange={(e) => setFilters(f => ({ ...f, effectiveDate: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Dates</option>
                <option value="jan2025">Jan 1, 2025</option>
                <option value="jan2026">Jan 1, 2026</option>
                <option value="past">Past (Expired)</option>
                <option value="future">Future</option>
              </select>
            </div>
          </div>

          {/* Clear Filters */}
          {activeCount > 0 && (
            <div className="mt-4 pt-3 border-t border-slate-100 flex justify-end">
              <button
                onClick={onClear}
                className="px-4 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg flex items-center gap-1"
              >
                <X size={14} />
                Clear All Filters
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------- Main Page ---------- */
// Risk category to score range mapping (matches churn prediction engine)
const RISK_RANGES = {
  stable: { min: 0, max: 19 },
  low: { min: 20, max: 34 },
  medium: { min: 35, max: 49 },
  moderate: { min: 35, max: 49 },  // Alias for medium
  elevated: { min: 50, max: 64 },
  high: { min: 65, max: 79 },
  critical: { min: 80, max: 89 },
  severe: { min: 90, max: 100 }
};

export default function Clients() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const riskCategory = searchParams.get('risk_category');
  const urlStatus = searchParams.get('status');
  const urlFilter = searchParams.get('filter');
  const urlSort = searchParams.get('sort');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [sortBy, setSortBy] = useState(urlSort || 'name_asc');

  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmIds, setConfirmIds] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selected, setSelected] = useState({});

  // Advanced filters
  const [filters, setFilters] = useState({
    state: '',
    city: '',
    carrier: '',
    plan: '',
    planType: '',
    status: '',
    riskLevel: '',
    effectiveDate: '',
  });

  // Import history
  const [importHistory, setImportHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [reversingId, setReversingId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historyMsg, setHistoryMsg] = useState("");

  // Mobile action menu
  const [showMobileActions, setShowMobileActions] = useState(false);

  // Duplicate detection state
  const [duplicateModal, setDuplicateModal] = useState(false);
  const [duplicateData, setDuplicateData] = useState(null);
  const [pendingPayload, setPendingPayload] = useState(null);

  async function load() {
    setErr("");
    try {
      setLoading(true);
      const data = await getClients();
      data.sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")));
      setRows(data);
    } catch (e) {
      setErr("Failed to load clients");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  // Initialize filters from URL params
  useEffect(() => {
    if (urlStatus) {
      setFilters(prev => ({ ...prev, status: urlStatus }));
    }
  }, [urlStatus]);

  // Sync sort from URL param
  useEffect(() => {
    if (urlSort && urlSort !== sortBy) {
      setSortBy(urlSort);
    }
  }, [urlSort]);

  const loadImportHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const data = await getImportHistory();
      setImportHistory(data);
    } catch (e) {
      console.error('[IMPORT] Failed to load import history:', e);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    if (showHistory && importHistory.length === 0) {
      loadImportHistory();
    }
  }, [showHistory, loadImportHistory, importHistory.length]);

  const handleReverseImport = async (importId) => {
    if (!window.confirm('Are you sure you want to reverse this import? This will permanently delete all clients that were added in this batch.')) {
      return;
    }
    setReversingId(importId);
    try {
      const result = await reverseImport(importId);
      setHistoryMsg(`Reversed import: ${result.deletedCount} clients removed`);
      await loadImportHistory();
      await load();
    } catch (e) {
      setErr('Failed to reverse import: ' + (e?.message || 'Unknown error'));
    } finally {
      setReversingId(null);
    }
  };

  // Calculate unique filter options from data
  const filterOptions = useMemo(() => {
    const states = [...new Set(rows.map(r => r.state).filter(Boolean))].sort();
    const cities = [...new Set(rows.map(r => r.city).filter(Boolean))].sort();
    const carriers = [...new Set(rows.map(r => r.carrier).filter(Boolean))].sort();
    const plans = [...new Set(rows.map(r => r.plan).filter(Boolean))].sort();
    const planTypes = [...new Set(rows.map(r => r.planType).filter(Boolean))].sort();
    const statuses = [...new Set(rows.map(r => r.status).filter(Boolean))].sort();

    return { states, cities, carriers, plans, planTypes, statuses };
  }, [rows]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    return Object.values(filters).filter(v => v !== '').length + (riskCategory ? 1 : 0) + (urlFilter ? 1 : 0);
  }, [filters, riskCategory, urlFilter]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters({
      state: '',
      city: '',
      carrier: '',
      plan: '',
      planType: '',
      status: '',
      riskLevel: '',
      effectiveDate: '',
    });
    setSearchParams({});
  }, [setSearchParams]);

  const filtered = useMemo(() => {
    let result = rows;

    // Handle special URL filters
    if (urlFilter === 'new_this_month') {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      result = result.filter(r => {
        const createdAt = r.createdAt ? new Date(r.createdAt) : null;
        return createdAt && createdAt >= startOfMonth;
      });
    } else if (urlFilter === 'at_risk') {
      result = result.filter(r => {
        const score = r.riskScore ?? r.risk_score ?? 0;
        return score >= 50; // Elevated risk and above
      });
    }

    // Filter by risk category if present in URL
    if (riskCategory && RISK_RANGES[riskCategory]) {
      const range = RISK_RANGES[riskCategory];
      result = result.filter((r) => {
        const score = r.riskScore ?? r.risk_score ?? 0;
        return score >= range.min && score <= range.max;
      });
    }

    // Apply advanced filters
    if (filters.state) {
      result = result.filter(r => r.state === filters.state);
    }
    if (filters.city) {
      result = result.filter(r => r.city === filters.city);
    }
    if (filters.carrier) {
      result = result.filter(r => r.carrier === filters.carrier);
    }
    if (filters.plan) {
      result = result.filter(r => r.plan === filters.plan);
    }
    if (filters.planType) {
      result = result.filter(r => r.planType === filters.planType);
    }
    if (filters.status) {
      result = result.filter(r => r.status === filters.status);
    }
    if (filters.riskLevel && RISK_RANGES[filters.riskLevel]) {
      const range = RISK_RANGES[filters.riskLevel];
      result = result.filter(r => {
        const score = r.riskScore ?? r.risk_score ?? 0;
        return score >= range.min && score <= range.max;
      });
    }
    if (filters.effectiveDate) {
      const today = new Date();
      result = result.filter(r => {
        if (!r.effectiveDate) return false;
        const effDate = new Date(r.effectiveDate);
        switch (filters.effectiveDate) {
          case 'jan2025':
            return effDate.getFullYear() === 2025 && effDate.getMonth() === 0 && effDate.getDate() === 1;
          case 'jan2026':
            return effDate.getFullYear() === 2026 && effDate.getMonth() === 0 && effDate.getDate() === 1;
          case 'past':
            return effDate < today;
          case 'future':
            return effDate >= today;
          default:
            return true;
        }
      });
    }

    // Filter by search query
    const q = search.trim().toLowerCase();
    if (q) {
      result = result.filter((r) => {
        const hay = `${r.firstName} ${r.lastName} ${r.email} ${r.phone} ${r.carrier} ${r.plan} ${r.city} ${r.state}`.toLowerCase();
        return hay.includes(q);
      });
    }

    // Apply sort based on sortBy
    result.sort((a, b) => {
      switch (sortBy) {
        case 'name_desc': {
          const lastA = (a.lastName || '').toLowerCase();
          const lastB = (b.lastName || '').toLowerCase();
          if (lastA !== lastB) return lastB.localeCompare(lastA);
          return (b.firstName || '').toLowerCase().localeCompare((a.firstName || '').toLowerCase());
        }
        case 'newest':
          return String(b.createdAt || '').localeCompare(String(a.createdAt || ''));
        case 'oldest':
          return String(a.createdAt || '').localeCompare(String(b.createdAt || ''));
        case 'risk_score': {
          const scoreA = a.riskScore ?? a.risk_score ?? 0;
          const scoreB = b.riskScore ?? b.risk_score ?? 0;
          return scoreB - scoreA;
        }
        case 'name_asc':
        default: {
          const lastA = (a.lastName || '').toLowerCase();
          const lastB = (b.lastName || '').toLowerCase();
          if (lastA !== lastB) return lastA.localeCompare(lastB);
          return (a.firstName || '').toLowerCase().localeCompare((b.firstName || '').toLowerCase());
        }
      }
    });

    return result;
  }, [rows, search, riskCategory, urlFilter, filters, sortBy]);

  const selectedIds = useMemo(() => Object.keys(selected).filter((id) => selected[id]), [selected]);

  return (
    <div className="px-0 sm:px-6 py-4 sm:py-6">
      {/* Header */}
      <div className="px-4 sm:px-0 mb-4" data-tour="clients-header">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">{t('clients')}</h1>
              <span className="px-2.5 py-1 bg-slate-100 text-slate-700 text-sm font-semibold rounded-full">
                {rows.length}
              </span>
            </div>
            <p className="text-sm text-slate-600">{t('allYourClients')}</p>
          </div>

          {/* Desktop Actions */}
          <div className="hidden sm:flex items-center gap-3">
            <button
              className="px-4 py-2 bg-[#FFB800] text-[#172A3A] font-semibold rounded-lg shadow hover:opacity-90 flex items-center gap-2"
              onClick={() => { setEditRow(null); setShowForm(true); }}
            >
              <Plus size={18} />
              {t('addClient')}
            </button>

            <button
              data-tour="clients-import-btn"
              className="px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center gap-2"
              onClick={() => nav("/clients/import")}
            >
              <Upload size={16} /> {t('import')}
            </button>

            <a
              className="px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center gap-2"
              href={getExportCsvUrl()}
            >
              <Download size={16} /> {t('export')}
            </a>

            <button
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 px-3 py-2 rounded-lg border border-blue-200 hover:bg-blue-50"
            >
              <History size={14} />
              {showHistory ? 'Hide' : 'View'} History
            </button>
          </div>

          {/* Mobile Add Button */}
          <div className="sm:hidden flex items-center gap-2">
            <button
              className="flex-1 px-4 py-3 bg-[#FFB800] text-[#172A3A] font-semibold rounded-xl shadow hover:opacity-90 flex items-center justify-center gap-2"
              onClick={() => { setEditRow(null); setShowForm(true); }}
            >
              <Plus size={18} />
              {t('addClient')}
            </button>
            <button
              onClick={() => setShowMobileActions(!showMobileActions)}
              className="p-3 rounded-xl border border-slate-200 hover:bg-slate-50"
            >
              <MoreHorizontal size={20} />
            </button>
          </div>
        </div>

        {/* Mobile Actions Dropdown */}
        {showMobileActions && (
          <div className="sm:hidden mt-3 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
            <button
              className="w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-50 border-b border-slate-100"
              onClick={() => { nav("/clients/import"); setShowMobileActions(false); }}
            >
              <Upload size={18} className="text-slate-500" />
              <span>{t('import')}</span>
            </button>
            <a
              className="w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-50 border-b border-slate-100"
              href={getExportCsvUrl()}
              onClick={() => setShowMobileActions(false)}
            >
              <Download size={18} className="text-slate-500" />
              <span>{t('export')}</span>
            </a>
            <button
              className="w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-50"
              onClick={() => { setShowHistory(!showHistory); setShowMobileActions(false); }}
            >
              <History size={18} className="text-slate-500" />
              <span>{showHistory ? 'Hide' : 'View'} Import History</span>
            </button>
          </div>
        )}
      </div>

      {/* Risk Category Filter Banner */}
      {riskCategory && RISK_RANGES[riskCategory] && (
        <div className="mx-4 sm:mx-0 mb-4 bg-blue-50 rounded-xl p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-blue-700">
              Showing <span className="font-semibold capitalize">{riskCategory}</span> risk clients
              <span className="text-blue-500 ml-1">
                (score {RISK_RANGES[riskCategory].min}-{RISK_RANGES[riskCategory].max})
              </span>
            </span>
          </div>
          <button
            onClick={() => setSearchParams({})}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-lg"
          >
            <X size={14} />
            Clear filter
          </button>
        </div>
      )}

      {/* Success message */}
      {historyMsg && (
        <div className="mx-4 sm:mx-0 mb-4 bg-green-50 text-green-700 text-sm px-4 py-3 rounded-xl border border-green-200 flex items-center justify-between">
          <span>{historyMsg}</span>
          <button onClick={() => setHistoryMsg("")} className="text-green-500 hover:text-green-700 p-1">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Import History Panel */}
      {showHistory && (
        <div className="mx-4 sm:mx-0 mb-4 bg-white rounded-xl shadow-sm ring-1 ring-black/5 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <History size={16} className="text-blue-500" />
              Recent Bulk Imports
            </h3>
            <button
              onClick={loadImportHistory}
              disabled={loadingHistory}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              {loadingHistory ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
              Refresh
            </button>
          </div>

          {loadingHistory ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
            </div>
          ) : importHistory.length === 0 ? (
            <div className="text-center py-6 text-slate-500">
              <Clock size={24} className="mx-auto mb-2 text-slate-300" />
              <p className="text-sm">No import history found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {importHistory.map((imp) => {
                const isReversed = imp.status === 'reversed';
                const date = new Date(imp.created_at);
                const formattedDate = date.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit'
                });

                return (
                  <div
                    key={imp.id}
                    className={`p-3 rounded-lg border ${
                      isReversed ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-medium text-slate-800 text-sm truncate">
                            {imp.filename || 'Unnamed Import'}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            isReversed ? 'bg-gray-200 text-gray-600' : 'bg-green-100 text-green-700'
                          }`}>
                            {imp.status}
                          </span>
                        </div>
                        <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                          <span className="flex items-center gap-1">
                            <Clock size={12} />
                            {formattedDate}
                          </span>
                          <span className="text-green-600">{imp.created_count} added</span>
                          {imp.skipped_count > 0 && (
                            <span className="text-yellow-600">{imp.skipped_count} skipped</span>
                          )}
                        </div>
                      </div>

                      {!isReversed && (
                        <button
                          onClick={() => handleReverseImport(imp.id)}
                          disabled={reversingId === imp.id}
                          className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 disabled:opacity-50 flex-shrink-0"
                        >
                          {reversingId === imp.id ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : (
                            <RotateCcw size={12} />
                          )}
                          Undo
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Search Bar + Sort */}
      <div className="px-4 sm:px-0 mb-4">
        <div className="flex gap-2 items-center">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="w-full pl-10 pr-4 py-3 sm:py-2 border border-slate-200 rounded-xl sm:rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('searchNameEmailCarrier')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value);
              const newParams = new URLSearchParams(searchParams);
              if (e.target.value === 'name_asc') {
                newParams.delete('sort');
              } else {
                newParams.set('sort', e.target.value);
              }
              setSearchParams(newParams);
            }}
            className="px-3 py-3 sm:py-2 border border-slate-200 rounded-xl sm:rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="name_asc">Name A-Z</option>
            <option value="name_desc">Name Z-A</option>
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="risk_score">Risk Score</option>
          </select>
        </div>
      </div>

      {/* Advanced Filters Panel */}
      <div className="px-4 sm:px-0 mb-4">
        <FilterPanel
          filters={filters}
          setFilters={setFilters}
          filterOptions={filterOptions}
          onClear={clearFilters}
          activeCount={activeFilterCount}
        />
      </div>

      {/* Filter Results Summary */}
      {(activeFilterCount > 0 || search) && (
        <div className="px-4 sm:px-0 mb-4">
          <div className="text-sm text-slate-600">
            Showing <span className="font-semibold text-slate-900">{filtered.length}</span> of{' '}
            <span className="font-semibold text-slate-900">{rows.length}</span> clients
            {activeFilterCount > 0 && (
              <span className="ml-2 text-blue-600">
                ({activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} applied)
              </span>
            )}
          </div>
        </div>
      )}

      {/* Bulk Actions (when items selected) */}
      {selectedIds.length > 0 && (
        <div className="mx-4 sm:mx-0 mb-4 bg-blue-50 rounded-xl p-3 flex items-center justify-between">
          <span className="text-sm text-blue-700 font-medium">
            {selectedIds.length} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              className="p-2 rounded-lg bg-white border border-blue-200 text-blue-600 hover:bg-blue-100"
              onClick={() => setConfirmIds(selectedIds)}
            >
              <Trash2 size={18} />
            </button>
            <button
              className="p-2 rounded-lg bg-white border border-blue-200 text-blue-600 hover:bg-blue-100"
              onClick={() => {
                alert(`Bulk email to ${selectedIds.length} selected client${selectedIds.length !== 1 ? 's' : ''} — coming soon.`);
              }}
              title="Send bulk email"
            >
              <Mail size={18} />
            </button>
            <button
              className="p-2 rounded-lg bg-white border border-blue-200 text-blue-600 hover:bg-blue-100"
              onClick={() => {
                alert(`Bulk SMS to ${selectedIds.length} selected client${selectedIds.length !== 1 ? 's' : ''} — coming soon.`);
              }}
              title="Send bulk SMS"
            >
              <MessageSquare size={18} />
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {err && (
        <div className="mx-4 sm:mx-0 mb-4 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-xl border border-red-200">
          {err}
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <User size={48} className="mx-auto mb-3 text-slate-300" />
          <p className="text-lg font-medium">{t('noClients')}</p>
          <p className="text-sm mt-1">Add your first client to get started</p>
        </div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="sm:hidden px-4 space-y-3">
            {filtered.map((client) => (
              <ClientCard
                key={client.id}
                client={client}
                selected={!!selected[client.id]}
                onSelect={(checked) => setSelected((s) => ({ ...s, [client.id]: checked }))}
                onEdit={(c) => { setEditRow(c); setShowForm(true); }}
                onDelete={(id) => setConfirmIds([id])}
                t={t}
              />
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden sm:block bg-white rounded-xl shadow-sm ring-1 ring-black/5 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-4 py-3 w-10">
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded"
                        checked={filtered.length > 0 && filtered.every((r) => selected[r.id])}
                        onChange={(e) => {
                          const all = {};
                          if (e.target.checked) filtered.forEach((r) => (all[r.id] = true));
                          setSelected(all);
                        }}
                      />
                    </th>
                    <th className="px-4 py-3 text-left font-semibold">{t('name')}</th>
                    <th className="px-4 py-3 text-left font-semibold">{t('phone')}</th>
                    <th className="px-4 py-3 text-left font-semibold">{t('email')}</th>
                    <th className="px-4 py-3 text-left font-semibold">{t('carrier')}</th>
                    <th className="px-4 py-3 text-left font-semibold">{t('plan')}</th>
                    <th className="px-4 py-3 text-left font-semibold">{t('created')}</th>
                    <th className="px-4 py-3 text-right font-semibold">{t('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => (
                    <tr key={c.id} className="border-t hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded"
                          checked={!!selected[c.id]}
                          onChange={(e) => setSelected((s) => ({ ...s, [c.id]: e.target.checked }))}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <Link to={`/clients/${c.id}`} className="text-blue-600 hover:underline font-medium">
                          {c.firstName} {c.lastName}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{c.phone || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{c.email || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{c.carrier || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{c.plan || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">
                        {c.createdAt ? new Date(c.createdAt).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          className="text-blue-600 hover:underline mr-3"
                          onClick={() => { setEditRow(c); setShowForm(true); }}
                        >
                          {t('edit')}
                        </button>
                        <button
                          className="text-red-600 hover:underline"
                          onClick={() => setConfirmIds([c.id])}
                        >
                          {t('delete')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Record Count */}
      <div className="px-4 sm:px-0 mt-4 text-xs text-slate-500">
        {t('totalRecords', { count: rows.length })}
      </div>

      {/* Add/Edit Modal */}
      {showForm && (
        <Modal title={editRow ? t('editClient') : t('addClient')} onClose={() => { setShowForm(false); setEditRow(null); }}>
          <ClientFormFull
            initial={editRow}
            saving={saving}
            onCancel={() => { setShowForm(false); setEditRow(null); }}
            onSave={async (form) => {
              try {
                setSaving(true);
                if (editRow && editRow.id) {
                  await updateClient(editRow.id, form);
                  await load();
                  setShowForm(false);
                  setEditRow(null);
                } else {
                  const result = await addClient(form);
                  console.log('[Clients] addClient result:', result);
                  // Check for duplicate
                  if (result && result.isDuplicate) {
                    console.log('[Clients] Duplicate detected, showing modal');
                    setPendingPayload(form);
                    setDuplicateData(result.duplicate);
                    setDuplicateModal(true);
                    setShowForm(false);
                    setSaving(false);
                    return;
                  }
                  await load();
                  setShowForm(false);
                  setEditRow(null);
                }
              } catch (e) {
                console.error('[Clients] Error:', e);
                alert(e.message || t('saveFailed'));
              } finally {
                setSaving(false);
              }
            }}
          />
        </Modal>
      )}

      {/* Delete Confirm */}
      {confirmIds && confirmIds.length > 0 && (
        <Confirm
          t={t}
          loading={deleting}
          text={confirmIds.length === 1
            ? t('deleteThisClient')
            : t('deleteMultipleClients', { count: confirmIds.length }) || `Delete ${confirmIds.length} clients?`}
          onCancel={() => setConfirmIds(null)}
          onConfirm={async () => {
            setDeleting(true);
            let successCount = 0;
            let failCount = 0;

            for (const id of confirmIds) {
              try {
                await deleteClient(id);
                successCount++;
              } catch (err) {
                failCount++;
                console.error('[DELETE] Failed to delete client:', id, err);
              }
            }

            setDeleting(false);
            setConfirmIds(null);
            setSelected({});
            await load();

            if (failCount > 0) {
              alert(t('deletePartialFail', { success: successCount, fail: failCount }) ||
                `Deleted ${successCount} clients. ${failCount} failed.`);
            }
          }}
        />
      )}

      {/* Duplicate Detection Modal */}
      <DuplicateMergeModal
        isOpen={duplicateModal}
        duplicate={duplicateData}
        onClose={() => {
          setDuplicateModal(false);
          setDuplicateData(null);
          setPendingPayload(null);
        }}
        onSave={async (importIndex, mergedRecord) => {
          try {
            setSaving(true);
            setDuplicateModal(false);
            await addClient({
              mergeInto: duplicateData.existingClient.id,
              mergedData: mergedRecord
            });
            await load();
          } catch (e) {
            console.error(e);
            alert(e.message || 'Merge failed');
          } finally {
            setSaving(false);
            setDuplicateData(null);
            setPendingPayload(null);
          }
        }}
        onSkip={async () => {
          try {
            setSaving(true);
            setDuplicateModal(false);
            await addClient({
              ...pendingPayload,
              skipDuplicateCheck: true
            });
            await load();
          } catch (e) {
            console.error(e);
            alert(e.message || 'Create failed');
          } finally {
            setSaving(false);
            setDuplicateData(null);
            setPendingPayload(null);
          }
        }}
        currentIndex={1}
        totalDuplicates={1}
      />
    </div>
  );
}
